/**
 * Inline-editable rule card for pharmacy order update configurations.
 */

import { ToggleSwitch } from "@risalabs_frontend_org/oasis-ui-kit";
import type {
  QueryBuilderConfig,
  QueryBuilderOption,
  QueryBuilderValue,
} from "../../data-model/queryBuilder";
import type { CallingRuleAction } from "../../pages/configurations/pages/callingConfiguration/callingRuleUtils";
import ConditionsCard from "../conditionsCrad/conditionsCard";
import CallingRuleActionsSection from "../modals/AddNewCallingRuleModal/CallingRuleActionsSection";
import QueryBuilder from "../queryBuilder/QueryBuilder";

interface PharmacyOrderUpdateRuleCardProps {
  ruleId: string;
  label: string;
  subtext?: string;
  active: boolean;
  condition: QueryBuilderValue;
  actions: CallingRuleAction[];
  conditionConfig: QueryBuilderConfig;
  actionValueConfig: QueryBuilderConfig;
  actionFieldOptions: QueryBuilderOption[];
  configKey: string;
  ruleLoadedVersion: number;
  defaultSubtext: string;
  onLabelChange: (value: string) => void;
  onSubtextChange: (value: string) => void;
  onActiveToggle: () => void;
  onConditionChange: (value: QueryBuilderValue) => void;
  onActionFieldChange: (index: number, value: string) => void;
  onActionTypeChange: (index: number, value: string) => void;
  onActionStringValueChange: (index: number, value: string) => void;
  onActionMultiValueChange: (index: number, values: string[]) => void;
  onAddAction: () => void;
  onRemoveAction: (index: number) => void;
  onDelete: () => void;
}

/** Renders a rule card with click-to-edit label/subtext, header active toggle, and editable conditions/actions. */
const PharmacyOrderUpdateRuleCard = ({
  ruleId,
  label,
  subtext,
  active,
  condition,
  actions,
  conditionConfig,
  actionValueConfig,
  actionFieldOptions,
  configKey,
  ruleLoadedVersion,
  defaultSubtext,
  onLabelChange,
  onSubtextChange,
  onActiveToggle,
  onConditionChange,
  onActionFieldChange,
  onActionTypeChange,
  onActionStringValueChange,
  onActionMultiValueChange,
  onAddAction,
  onRemoveAction,
  onDelete,
}: PharmacyOrderUpdateRuleCardProps) => (
  <ConditionsCard
    editableLabel={label}
    onLabelChange={onLabelChange}
    editableSubtext={subtext ?? ""}
    onSubtextChange={onSubtextChange}
    subtextPlaceholder={defaultSubtext}
    headerInputKey={`${ruleId}-${ruleLoadedVersion}`}
    headerClickable={false}
    defaultExpanded
    headerExtra={
      <div className="flex items-center gap-2">
        <span
          className={`text-xs font-semibold ${
            active ? "text-green-700" : "text-red-700"
          }`}
        >
          {active ? "Active" : "Inactive"}
        </span>
        <ToggleSwitch
          key={`rule-active-${ruleId}-${active}`}
          id={`rule-active-${ruleId}`}
          defaultChecked={active}
          onChangeEmit={onActiveToggle}
        />
      </div>
    }
    onDeleteStageClick={onDelete}
  >
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 rounded-md border border-primaryGray-14 p-4">
        <div className="text-sm font-semibold">Condition</div>
        <QueryBuilder
          key={`qb-${ruleId}-${ruleLoadedVersion}-${configKey}`}
          value={condition}
          onChange={onConditionChange}
          config={conditionConfig}
        />
      </div>
      <CallingRuleActionsSection
        conditionConfig={actionValueConfig}
        fieldOptions={actionFieldOptions}
        actions={actions}
        showHelperText={false}
        onAddAction={onAddAction}
        onRemoveAction={onRemoveAction}
        onTypeChange={onActionTypeChange}
        onFieldChange={onActionFieldChange}
        onStringValueChange={onActionStringValueChange}
        onMultiValueChange={onActionMultiValueChange}
      />
    </div>
  </ConditionsCard>
);

export default PharmacyOrderUpdateRuleCard;
